import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react'

import { View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { router } from 'expo-router'
import { getCreatePageStyles } from '../../../appStyles/_create.styles'
import { useAnalytics } from '../../../src/analytics/useAnalytics'
import { ANALYTICS_EVENTS } from '../../../src/analytics'
import { HOME_PATH } from '../../../src/home/constants/home.constants'
import { getCreateCalendarSkeletonStyles } from '../../../appStyles/_createSkeleton.styles'
import { useTheme } from '../../../src/theme/useTheme'
import { useCreatePage } from '../../../src/create/contexts/CreatePageContext'
import {
  DecorativeElements,
  CreateParticipantsStep,
  CreateBuildStep,
  CreateReviewStep,
  SuccessModal,
} from '../../../components/create'
import { CreateGamesStepSkeleton } from '../../../components/create/CreateGamesStep/CreateGamesStepSkeleton'
import { getCreateCalendarHeaderStyles } from '../../../components/create/CreateCalendarHeader/CreateCalendarHeader.styles'
import { Skeleton } from '../../../components/ui/Skeleton'
import { PageHeader, ErrorBoundary } from '../../../components/layout'
import { HeaderBackButton } from '../../../components/layout/HeaderBackButton/HeaderBackButton'
import { HEADER_FLAVORS } from '../../../components/layout/constants/pageHeader.constants'
import { useGuestWalkthroughStore } from '../../../src/shared/stores/guestWalkthroughStore'
import { useFirstChallengeCreatePublisher } from '../../../src/onboarding/hooks/useFirstChallengeCreatePublisher'

/** Placeholder for calendar strip so skeleton layout matches real create page (no useCreatePage). Full width like CalendarStrip. */
function CreateCalendarSkeleton() {
  const theme = useTheme()
  const styles = useMemo(() => getCreateCalendarSkeletonStyles(theme), [theme])
  return (
    <View style={styles.calendarStrip}>
      <View style={styles.headerRow}>
        <Skeleton width={28} height={28} borderRadius={14} />
        <Skeleton width={100} height={14} borderRadius={4} />
        <Skeleton width={28} height={28} borderRadius={14} />
      </View>
      <View style={styles.datesRow}>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
          <Skeleton key={i} width={32} height={36} borderRadius={8} />
        ))}
      </View>
    </View>
  )
}

/** Lightweight first-paint shell so create screen appears immediately (no useCreatePage). Replaced by CreateContent after first frame. */
function CreatePageSkeletonScreen() {
  const theme = useTheme()
  const styles = useMemo(() => getCreatePageStyles(theme), [theme])
  const calendarHeaderStyles = useMemo(() => getCreateCalendarHeaderStyles(theme), [theme])
  const contentWithCalendarTop = useMemo(
    () => [styles.content, styles.contentContainerGames],
    [styles]
  )
  return (
    <View style={styles.container}>
      <PageHeader flavor={HEADER_FLAVORS.CHALLENGES} props={{}} />
      <SafeAreaView style={styles.safeArea} edges={['left', 'right']}>
        <View style={calendarHeaderStyles.fixedCalendarContainer}>
          <View style={calendarHeaderStyles.calendarWrapper}>
            <CreateCalendarSkeleton />
          </View>
        </View>
        <View style={contentWithCalendarTop}>
          <CreateGamesStepSkeleton />
        </View>
      </SafeAreaView>
    </View>
  )
}

function CreateContent() {
  const theme = useTheme()
  const styles = useMemo(() => getCreatePageStyles(theme), [theme])
  const { track } = useAnalytics()
  const prevStepRef = useRef<string | null>(null)
  const dismissWalkthrough = useGuestWalkthroughStore((s) => s.dismissWalkthrough)

  const {
    step,
    setStep,
    back,
    setPickSlips,
    setSelectedGroupId,
    setSelectedGroup,
    setSelectedFriendId,
    setActiveTab,
    setDeckIdx,
    setStakeCoins,
    pickSlips,
    totalPoints,
    selectedGroupId,
    selectedGroup,
    selectedFriendId,
    friends,
    games,
  } = useCreatePage()

  // Publish create step + pick count to the first-challenge tour (inert unless the tour is active).
  useFirstChallengeCreatePublisher()

  // Must be declared after `step` is destructured from useCreatePage(). Referencing
  // `step` in the deps array above the destructure throws a TDZ error in production
  // web builds ("Cannot access 'step' before initialization").
  const handleCloseCreate = useCallback(() => {
    track(ANALYTICS_EVENTS.CREATE_CHALLENGE_ABANDONED, { step })
    dismissWalkthrough()
    // Home is the lobby (specs/home-page AC-4b, superseding challenge-flow-redesign AC-6.3's Arena).
    router.replace(HOME_PATH)
  }, [dismissWalkthrough, track, step])

  // Header back is step-aware (AC-6.1/6.2): from build/review, step back one; from the
  // rival picker (the first step), abandon the flow and return to Home.
  const handleHeaderBack = useCallback(() => {
    if (step === 'build' || step === 'review') {
      back()
      return
    }
    handleCloseCreate()
  }, [step, back, handleCloseCreate])

  useEffect(() => {
    track(ANALYTICS_EVENTS.CREATE_CHALLENGE_STARTED)
  }, [track])

  useEffect(() => {
    if (step === 'success') {
      track(ANALYTICS_EVENTS.CHALLENGE_CREATED, {
        participants_count: friends?.length,
        games_count: games?.length,
      })
      return
    }
    // Rival-first order: rival → build → review (specs/challenge-flow-redesign).
    if (prevStepRef.current === 'rival' && step === 'build') {
      track(ANALYTICS_EVENTS.CREATE_CHALLENGE_STEP_COMPLETED, { step: 'rival' })
    }
    if (prevStepRef.current === 'build' && step === 'review') {
      track(ANALYTICS_EVENTS.CREATE_CHALLENGE_STEP_COMPLETED, { step: 'build' })
    }
    prevStepRef.current = step
  }, [step, track, friends?.length, games?.length])

  const handleSuccessClose = useCallback(() => {
    setPickSlips([])
    setSelectedGroupId(null)
    setSelectedGroup(null)
    setSelectedFriendId(null)
    setActiveTab('friends')
    // Intentionally NOT resetting buildView: the Build screen remembers the last view the user
    // had open (deck/list, persisted) and the next challenge starts there (T-53).
    setDeckIdx(0)
    // The stake belongs to the challenge just sent — never carry it into the next one.
    setStakeCoins(0)
    setStep('rival')
    // After creating a challenge, return to Arena (the create flow's home).
    // `/app/` would redirect to /app/worldcup under the new default-tab rule.
    router.push('/app/arena')
  }, [
    setPickSlips,
    setSelectedGroupId,
    setSelectedGroup,
    setSelectedFriendId,
    setActiveTab,
    setDeckIdx,
    setStakeCoins,
    setStep,
  ])

  // Auto-navigate to active board after 10 seconds (user can tap to close earlier)
  React.useEffect(() => {
    if (step === 'success') {
      const timer = setTimeout(() => {
        handleSuccessClose()
      }, 5000)

      return () => clearTimeout(timer)
    }
  }, [step, handleSuccessClose])

  return (
    <View
      style={styles.container}
    >
      <PageHeader
        flavor={HEADER_FLAVORS.CHALLENGES}
        leftContent={<HeaderBackButton onPress={handleHeaderBack} testID="create-close-button" />}
      />
      <DecorativeElements />

      <SafeAreaView style={styles.safeArea} edges={['left', 'right']}>
        {step === 'rival' && (
          <View style={[styles.content, styles.contentContainerUserGroups]} key="rival">
            <CreateParticipantsStep />
          </View>
        )}

        {step === 'build' && (
          <View style={[styles.content, styles.contentContainerBuild]} key="build">
            <CreateBuildStep />
          </View>
        )}

        {step === 'review' && (
          <View style={[styles.content, styles.contentContainerBetting]} key="review">
            <CreateReviewStep />
          </View>
        )}
      </SafeAreaView>

      <SuccessModal
        visible={step === 'success'}
        onClose={handleSuccessClose}
        selectedGroupId={selectedGroupId}
        selectedGroup={selectedGroup}
        selectedFriendId={selectedFriendId}
        friends={friends}
        pickSlips={pickSlips}
        games={games || []}
        totalPoints={totalPoints}
      />

    </View>
  )
}

export default function NewChallenge() {
  const [showFullContent, setShowFullContent] = useState(false)
  useEffect(() => {
    let cancelled = false
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) setShowFullContent(true)
      })
    })
    return () => {
      cancelled = true
      cancelAnimationFrame(id)
    }
  }, [])

  if (!showFullContent) {
    return <CreatePageSkeletonScreen key="initial-shell" />
  }
  return (
    <ErrorBoundary key="main-content">
      <CreateContent />
    </ErrorBoundary>
  )
}
